import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import CaraChat from "@/components/CaraChat";
import CarerCard from "@/components/CarerCard";
import BookingModal from "@/components/BookingModal";
import { ArrowRight, Home as HomeIcon, Building2, MessageSquare, ShieldCheck, BadgeCheck, Award, Umbrella, Search } from "lucide-react";

const NEEDS = [
  { icon: "🧓", t: "Elder & senior care", d: "Companionship, day & night attendants, dementia and post-retirement support", r: "Elder Care Attendant · Companion", cat: "elder", bg: "bg-coral-soft" },
  { icon: "🩺", t: "Nursing & clinical at home", d: "Post-operative care, injections, wound dressing, vitals and medication management", r: "Registered Nurse · GNM", cat: "nursing", bg: "bg-sage-soft" },
  { icon: "🦵", t: "Physio & rehabilitation", d: "Recovery after surgery, stroke or injury — at home or at a partner clinic", r: "Physiotherapist · OT · Speech", cat: "physio", bg: "bg-butter-soft" },
  { icon: "👶", t: "Mother & baby care", d: "Newborn care, Japa support, lactation help and trained, background-checked sitters", r: "Japa Maid · Newborn Carer", cat: "mother", bg: "bg-coral-soft" },
  { icon: "🧠", t: "Mental health & counselling", d: "Talk therapy and wellbeing support — online or in person, with credentialed professionals", r: "Counsellor · Psychologist", cat: "mental", bg: "bg-sage-soft" },
  { icon: "🎨", t: "Therapy & wellness", d: "Music and art therapy, yoga and guided meditation for recovery and everyday wellbeing", r: "Music Therapist · Yoga", cat: "wellness", bg: "bg-butter-soft" },
  { icon: "🤝", t: "Special needs & disability", d: "Daily-living assistance, behaviour support and dignified, trained one-on-one care", r: "Support Worker", cat: "personal", bg: "bg-coral-soft" },
  { icon: "🌏", t: "NRI family care", d: "Care for parents back home while you're abroad — with updates you can actually see", r: "Pay in USD/GBP · WhatsApp updates", cat: "elder", bg: "bg-sage-soft", nri: true },
];

const CHIPS = [
  ["🧓 Elder care at home", "elder"], ["🩺 Post-surgery nursing", "nursing"], ["👶 Newborn & mother care", "mother"],
  ["🦵 Physiotherapy visit", "physio"], ["🧠 Counselling", "mental"],
];

const TRUST = [
  { icon: ShieldCheck, t: "Aadhaar & ABHA verified", d: "Government-ID confirmed identity via UIDAI e-KYC. Not self-declared — machine-verified." },
  { icon: BadgeCheck, t: "Police & background checked", d: "Independent criminal background check via AuthBridge & BetterPlace on every active carer." },
  { icon: Award, t: "BookMyCarer Certified", d: "A competency credential — we test skills, we don't just take a certificate at face value." },
  { icon: Umbrella, t: "Insured & guaranteed", d: "Bookings covered by our insurer-backed Trust Guarantee — a promise with real cover behind it." },
];

export default function Home() {
  const nav = useNavigate();
  const [carers, setCarers] = useState([]);
  const [caps, setCaps] = useState([]);
  const [query, setQuery] = useState("");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    api.get("/carers").then((r) => setCarers(r.data.slice(0, 6)));
    api.get("/meta").then((r) => setCaps(r.data.capabilities));
  }, []);

  const goSearch = () => nav(`/find-care?q=${encodeURIComponent(query)}`);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="bmc-wrap pt-12 pb-16 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
        <div className="animate-fade-up">
          <div className="eyebrow">Start here</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-3">Who needs care <span className="text-coral">today?</span></h1>
          <p className="text-base md:text-lg text-ink-soft mt-4 max-w-lg">Tell us the situation. We'll match you to a verified carer near you — and you can book in a few taps.</p>

          <div className="mt-7 bg-card rounded-full shadow-bmc border border-line-2 p-2 flex items-center gap-2">
            <Search size={18} className="ml-3 text-ink-faint" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && goSearch()}
              placeholder="e.g. Night attendant for my father in Gachibowli"
              className="flex-1 bg-transparent text-sm focus:outline-none py-2" data-testid="hero-search" />
            <button onClick={goSearch} className="btn-pill bg-coral text-white w-11 h-11 justify-center shrink-0" data-testid="hero-search-btn"><ArrowRight size={18} /></button>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {CHIPS.map(([label, cat]) => (
              <button key={label} onClick={() => nav(`/find-care?cat=${cat}`)} className="text-xs bg-ivory-2 hover:bg-coral-soft text-ink-soft px-3 py-2 rounded-full transition-colors" data-testid={`chip-${cat}`}>{label}</button>
            ))}
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-3 mt-8">
            {[["100%", "Aadhaar & police verified"], ["4.8★", "Average carer rating"], ["< 2 hrs", "Median time to match"]].map(([n, l]) => (
              <div key={l}><p className="font-display font-bold text-2xl text-ink">{n}</p><p className="text-xs text-ink-soft">{l}</p></div>
            ))}
          </div>
        </div>

        <div className="animate-fade-up" style={{ animationDelay: ".1s" }}><CaraChat /></div>
      </section>

      {/* NEED GRID */}
      <section className="bg-ivory-2 py-16">
        <div className="bmc-wrap">
          <div className="eyebrow">Start here</div>
          <h2 className="text-3xl md:text-4xl font-bold mt-2">What do you need help with?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {NEEDS.map((n) => (
              <button key={n.t} onClick={() => nav(n.nri ? "/nri" : `/find-care?cat=${n.cat}`)} data-testid={`need-${n.cat}`}
                className="card-bmc p-5 text-left group">
                <span className={`grid place-items-center w-12 h-12 rounded-xl text-2xl ${n.bg}`}>{n.icon}</span>
                <h3 className="font-display font-bold text-ink mt-4 flex items-center justify-between">{n.t}<ArrowRight size={16} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all text-coral" /></h3>
                <p className="text-sm text-ink-soft mt-2">{n.d}</p>
                <p className="text-xs text-ink-faint mt-3">{n.r}</p>
              </button>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 bg-card border border-line-2 rounded-r p-4 text-sm text-ink-soft">
            <span className="font-semibold text-ink flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-butter" />Coming soon</span>
            {["Lab tests & diagnostics", "Medical equipment", "Home vaccination", "Medicine delivery", "Care abroad"].map((c) => <span key={c}>· {c}</span>)}
          </div>
        </div>
      </section>

      {/* DELIVERY MODES */}
      <section className="bmc-wrap py-16">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: HomeIcon, t: "At home", d: "A carer comes to you — hourly, full-day or live-in.", g: "linear-gradient(140deg,#EC5E3F,#D14A2C)" },
            { icon: Building2, t: "At a clinic", d: "Visit a partner clinic for therapy and assessments.", g: "linear-gradient(140deg,#7C9A78,#5E7C5B)" },
            { icon: MessageSquare, t: "Online & tele", d: "Video consults for counselling, follow-ups and advice.", g: "linear-gradient(140deg,#2A2420,#4a3f37)" },
          ].map((m) => (
            <div key={m.t} className="rounded-r-lg p-7 text-white" style={{ background: m.g }}>
              <m.icon size={28} />
              <h3 className="font-display font-bold text-xl mt-4 text-white">{m.t}</h3>
              <p className="text-sm text-white/80 mt-2">{m.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MARKETPLACE */}
      <section className="bmc-wrap pb-16">
        <div className="eyebrow">The marketplace</div>
        <h2 className="text-3xl md:text-4xl font-bold mt-2">Meet carers near you</h2>
        <p className="text-ink-soft mt-2 max-w-xl">Real, verified people — you choose who comes to your home, never a faceless allocation.</p>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {carers.map((c) => <CarerCard key={c.id} carer={c} onBook={setBooking} />)}
        </div>
        <div className="text-center mt-8">
          <button onClick={() => nav("/find-care")} className="btn-pill border border-line px-6 py-3 hover:border-coral" data-testid="browse-all">Browse all carers <ArrowRight size={16} /></button>
        </div>
      </section>

      {/* BROWSE CAPABILITY */}
      <section className="bg-ivory-2 py-14">
        <div className="bmc-wrap">
          <h2 className="text-2xl md:text-3xl font-bold">Browse by capability</h2>
          <div className="flex flex-wrap gap-2.5 mt-6">
            {caps.map(([role, count]) => (
              <button key={role} onClick={() => nav(`/find-care?q=${encodeURIComponent(role)}`)} className="bg-card border border-line rounded-full px-4 py-2 text-sm hover:border-coral transition-colors">
                {role} <span className="text-coral font-semibold">{count}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="bmc-wrap py-16">
        <div className="bg-ink text-ivory rounded-r-lg p-10">
          <div className="eyebrow text-butter">Why families trust us</div>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 text-ivory">Verified isn't a badge. It's the whole platform.</h2>
          <div className="grid md:grid-cols-4 gap-6 mt-8">
            {TRUST.map((t) => (
              <div key={t.t}>
                <t.icon size={28} className="text-butter" />
                <h3 className="font-display font-bold mt-3 text-ivory">{t.t}</h3>
                <p className="text-sm text-ivory/60 mt-2">{t.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NRI STRIP */}
      <section className="bmc-wrap pb-16">
        <div className="rounded-r-lg p-10 grid md:grid-cols-[1.3fr_1fr] gap-8 items-center" style={{ background: "linear-gradient(120deg,var(--butter-soft),var(--coral-soft))" }}>
          <div>
            <div className="eyebrow">For the diaspora</div>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Caring for parents back home, from anywhere</h2>
            <p className="text-ink-soft mt-3">Book and pay in your currency. Get WhatsApp updates, photos and care notes after every visit — in your timezone.</p>
            <div className="flex flex-wrap gap-3 mt-5">
              <button onClick={() => nav("/nri")} className="btn-pill bg-coral text-white px-5 py-2.5" data-testid="nri-cta">Set up NRI care</button>
              <button onClick={() => nav("/how-it-works")} className="btn-pill border border-ink/20 px-5 py-2.5">How updates work</button>
            </div>
          </div>
          <div className="text-5xl md:text-6xl text-center tracking-widest">🇺🇸 🇬🇧 🇦🇪 🇸🇬 🇦🇺</div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-ivory-2 py-16">
        <div className="bmc-wrap">
          <h2 className="text-3xl md:text-4xl font-bold text-center">How it works</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {[["1", "Tell us the situation", "Search or chat with Cara to describe who needs care."], ["2", "Choose your carer", "Compare verified profiles, reviews and rates. You pick."], ["3", "Book & pay securely", "UPI, card or international. Funds held in escrow."]].map(([n, t, d]) => (
              <div key={n} className="text-center">
                <span className="grid place-items-center w-12 h-12 rounded-full bg-coral text-white font-display font-bold mx-auto">{n}</span>
                <h3 className="font-display font-bold mt-4">{t}</h3>
                <p className="text-sm text-ink-soft mt-2">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BECOME A CARER BAND */}
      <section className="bmc-wrap py-16">
        <div className="bg-sage-soft rounded-r-lg p-10 grid md:grid-cols-[1.4fr_1fr] gap-6 items-center">
          <div>
            <h2 className="text-3xl font-bold">Build a career in care</h2>
            <p className="text-ink-soft mt-3">Join 300+ verified carers earning ₹400–₹1,500/day in Hyderabad and Bengaluru. Set your hours, work with verified families only.</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {["Free training", "Verified clients", "On-time payouts", "Safety-first"].map((p) => <span key={p} className="text-xs bg-card px-3 py-1.5 rounded-full">{p}</span>)}
            </div>
          </div>
          <div className="md:text-right">
            <button onClick={() => nav("/become-a-carer")} className="btn-pill bg-ink text-ivory px-6 py-3" data-testid="become-carer-cta">Become a carer <ArrowRight size={16} /></button>
          </div>
        </div>
      </section>

      <BookingModal carer={booking} open={!!booking} onClose={() => setBooking(null)} />
    </PublicLayout>
  );
}
